import { useT } from '@/i18n';
import type { OnlineOrder } from '@restaurant/api-client';
import { List, ListItem, Popover } from 'framework7-react';
import { forwardRef, type MutableRefObject } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

interface OrderActionsProps {
  order: OnlineOrder | null;
  onClose: () => void;
  onAccept: (order: OnlineOrder) => void;
  onComplete: (order: OnlineOrder) => void;
  onCancel: (order: OnlineOrder) => void;
}

/**
 * Contextual lifecycle popover - same control inside the inspector and on the
 * list card, so the operator memorises one pattern (mirrors
 * `ReservationActions`). Rows are server-driven: we only show what
 * `order.actions` permits, coloured by intent (green = forward move,
 * red = destructive) not by destination state.
 */
const OrderActions = forwardRef<PopoverInstance, OrderActionsProps>(function OrderActions(
  { order, onClose, onAccept, onComplete, onCancel },
  ref,
) {
  const t = useT();
  const renderContent = () => {
    if (!order) return null;
    const o = order;

    const item = (
      label: string,
      color: string,
      icon: string,
      onClick: () => void,
      testid?: string,
    ) => (
      <ListItem
        key={label}
        title={label}
        link="#"
        style={{ color }}
        {...(testid ? { 'data-testid': testid } : {})}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          onClose();
          onClick();
        }}
      >
        <i slot="media" className="f7-icons" style={{ color, fontSize: 22 }}>
          {icon}
        </i>
      </ListItem>
    );

    const list = (items: React.ReactNode) => (
      <List noChevron style={{ minWidth: 220, margin: 0 }}>
        {items}
      </List>
    );

    const forward = '#248a3d'; // vivid green - positive lifecycle move
    const destructive = '#ff3b30'; // red - cancel

    // Derive the menu from the order's status (same approach as
    // ReservationActions). The list endpoint does not populate the
    // server-computed `actions` array, so relying on it left this popover
    // empty. When `actions` *is* present (detail view) we intersect with it so
    // the server stays authoritative; the use-case still rejects any invalid
    // transition regardless.
    const serverGated = (a: string) => o.actions.length === 0 || o.actions.includes(a);
    // Bare "Accept" is only legal while it's the OWNER's turn (stage
    // NEEDS_ACCEPTANCE) - mirrors the server's canAcceptOrder guard. An order
    // still awaiting the customer's payment / a proof review confirms via its own
    // rail, so we don't offer Accept there. Fall back to status only when stage
    // is absent (older payloads).
    const ownerCanAccept = o.stage ? o.stage === 'NEEDS_ACCEPTANCE' : o.status === 'PENDING';
    const canAccept = ownerCanAccept && serverGated('accept');
    // A finished CASH order can still be corrected: the owner holds the money, so
    // a mis-tap is a counter correction rather than a refund. Mirrors the server's
    // canCorrectFinalizedOrder gate (cash only, no refund on the payment). The
    // method is only on query payloads, so a just-mutated order shows no
    // correction row until the list refetch lands - the use-case stays the truth.
    const cashCorrectable = o.paymentMethodKind === 'CASH';
    const undoComplete = o.status === 'COMPLETED' && cashCorrectable;
    const undoCancel = o.status === 'CANCELLED' && cashCorrectable;

    const canComplete = (o.status === 'CONFIRMED' || undoCancel) && serverGated('complete');
    const canCancel =
      (o.status === 'PENDING' || o.status === 'CONFIRMED' || undoComplete) && serverGated('cancel');

    if (!canAccept && !canComplete && !canCancel) return null;

    return (
      <>
        {(canAccept || canComplete) &&
          list(
            <>
              {canAccept &&
                item(t.orders.acceptOrder, forward, 'checkmark_circle_fill', () => onAccept(o))}
              {canComplete &&
                item(
                  undoCancel ? t.orders.restoreOrder : t.orders.markCompleted,
                  forward,
                  undoCancel ? 'arrow_uturn_left_circle' : 'checkmark_seal_fill',
                  () => onComplete(o),
                )}
            </>,
          )}
        {canCancel &&
          list(
            <>
              {item(
                undoComplete ? t.orders.undoCompletion : t.orders.cancelOrder,
                destructive,
                undoComplete ? 'arrow_uturn_left_circle' : 'xmark_circle',
                () => onCancel(o),
                'cancel-order-action',
              )}
            </>,
          )}
      </>
    );
  };

  return (
    <Popover
      ref={ref as MutableRefObject<PopoverInstance>}
      onPopoverClosed={onClose}
      // Tapping outside must dismiss the accept/cancel menu. F7's backdrop click
      // alone didn't (the anchored popover renders without one here), so also
      // close on any outside click.
      closeByOutsideClick
      backdrop
      style={{ minWidth: 220 }}
    >
      {renderContent()}
    </Popover>
  );
});

export default OrderActions;
